import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext, useContext } from 'react';
import type { IUserProfileApi } from './api';
import { UserProfileApi } from './api';
import type {
  FamilyMemberProfileDto,
  UpdateUserLocaleDto,
  UserProfileInterface,
} from '@pflegenavi/shared/api';
import {
  useAuthentication,
  useAuthenticationInternal,
} from '@pflegenavi/frontend/authentication';
import type {
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { USER_PROFILE_KEY } from './queryKeys';
import { useUserProfilePhoenixApi } from '../user-profile-phoenix';
import { USER_PROFILE_PHOENIX_KEY_APP_EMPLOYEE } from '../user-profile-phoenix/queryKeys';

const ApiContext = createContext<IUserProfileApi | undefined>(undefined);
const { useApi: useUserProfileApi, ApiProvider: UserProfileApiProvider } =
  makeApiProvider({
    name: 'UserProfile',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new UserProfileApi(tenantId, auth, apiUrl),
  });

export {
  useUserProfileApi,
  UserProfileApiProvider,
  ApiContext as UserProfileApiContext,
};
export type { IUserProfileApi } from './api';

export const ProfileTypeContext = createContext<
  'employee' | 'familyMember' | undefined
>(undefined);

/**
 * Will call the correct endpoint based on the context
 * For employees, uses the Phoenix API and transforms the response
 * For family members, uses the legacy API
 */
export const useUserProfileWithContext = (
  options: Omit<
    UseQueryOptions<UserProfileInterface, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<UserProfileInterface, unknown> => {
  const app = useContext(ProfileTypeContext);

  const auth = useAuthenticationInternal();

  const api = useUserProfileApi(options.enabled === false);
  const phoenixApi = useUserProfilePhoenixApi(options.enabled === false);

  if (app === 'employee') {
    // Use Phoenix API for employees
    // app is static
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useApiQuery(
      () => phoenixApi,
      USER_PROFILE_PHOENIX_KEY_APP_EMPLOYEE(auth.user?.userId ?? ''),
      async (api) => {
        const phoenixProfile = await api!.getEmployeeProfile();
        // Transform Phoenix response to match UserProfileInterface
        const transformedProfile: UserProfileInterface = {
          featureFlags: phoenixProfile.feature_flags || [],
          user: {
            id: phoenixProfile.user.id,
            firstName: phoenixProfile.user.first_name,
            lastName: phoenixProfile.user.last_name,
            eMail: phoenixProfile.user.email,
            locale: phoenixProfile.user.locale || undefined,
            createdAt: phoenixProfile.user.created_at
              ? new Date(phoenixProfile.user.created_at)
              : undefined,
          },
        };
        return transformedProfile;
      },
      {
        ...options,
        staleTime: options?.staleTime ?? Infinity,
      }
    );
  } else {
    // Use legacy API for family members
    // app is static
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useApiQuery(
      () => api,
      USER_PROFILE_KEY(auth.user?.userId ?? ''),
      (api) => {
        return api?.get() as unknown as Promise<FamilyMemberProfileDto>;
      },
      {
        ...options,
        staleTime: options?.staleTime ?? Infinity,
      }
    );
  }
};

export const useFamilyMemberProfile = (
  options: Omit<
    UseQueryOptions<FamilyMemberProfileDto, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<FamilyMemberProfileDto, unknown> => {
  const { user } = useAuthentication();

  const api = useUserProfileApi(options.enabled === false);

  return useApiQuery(
    () => api,
    USER_PROFILE_KEY(user?.userId ?? ''),
    (api) => {
      return api?.get() as unknown as Promise<FamilyMemberProfileDto>;
    },

    {
      ...options,
      staleTime: options?.staleTime ?? Infinity,
    }
  );
};

export const useUpdateUserProfile = (): UseMutationResult<
  UpdateUserLocaleDto,
  unknown,
  UpdateUserLocaleDto
> => {
  const api = useUserProfileApi();
  const queryClient = useQueryClient();
  const { user } = useAuthentication();

  return useMutation<UpdateUserLocaleDto, unknown, UpdateUserLocaleDto>({
    mutationKey: ['update_user'],
    mutationFn: (data) =>
      api?.updateUser(data) as unknown as Promise<{ id: string }>,
    onSuccess: async () => {
      return await queryClient.invalidateQueries({
        queryKey: USER_PROFILE_KEY(user?.userId ?? ''),
      });
    },
  });
};

export const usePostTest400 = (): UseMutationResult<void, unknown, void> => {
  const api = useUserProfileApi();

  return useMutation<void, unknown, void>({
    mutationKey: ['TEST_POST_400'],
    mutationFn: () => api?.test400Post(),
  });
};
